import { Suspense, useRef, useEffect, useMemo, useCallback } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment } from '@react-three/drei';
import * as THREE from 'three';
import CardModel, { CardColor, WhiteTreatment } from './CardModel';

// Environment presets available in drei
export type EnvironmentPreset = 'apartment' | 'city' | 'dawn' | 'forest' | 'lobby' | 'night' | 'park' | 'studio' | 'sunset' | 'warehouse';

// Animation constants (matching mobile app)
const FLOAT_AMPLITUDE = 0.08;
const FLOAT_PERIOD = 2000;
const ROTATE_AMPLITUDE = 0.12;
const ROTATE_PERIOD = 3000;
const INITIAL_TILT_X = -0.15;
const INITIAL_ROTATION_Y = 0.4;

// Rotation scale for drag
const ROTATION_SENSITIVITY = 0.01;
const MAX_TILT = Math.PI / 3; // 60 degrees

// Zoom settings
const MIN_SCALE = 0.5;
const MAX_SCALE = 2.5;
const ZOOM_SENSITIVITY = 0.001;

// Default background color
const DEFAULT_BG_COLOR = '#F5F2F2';

// Helper to create gradient colors from a base color
function createGradientColors(baseColor: string): { top: string; bottom: string } {
  const color = new THREE.Color(baseColor);
  const hsl = { h: 0, s: 0, l: 0 };
  color.getHSL(hsl);

  // Create lighter top and darker bottom
  const topColor = new THREE.Color().setHSL(hsl.h, hsl.s, Math.min(1, hsl.l + 0.08));
  const bottomColor = new THREE.Color().setHSL(hsl.h, hsl.s, Math.max(0, hsl.l - 0.08));

  return {
    top: '#' + topColor.getHexString(),
    bottom: '#' + bottomColor.getHexString(),
  };
}

interface GradientBackgroundProps {
  baseColor: string;
}

function GradientBackground({ baseColor }: GradientBackgroundProps) {
  const geometry = useMemo(() => {
    const { top, bottom } = createGradientColors(baseColor);
    const geo = new THREE.PlaneGeometry(100, 100);
    const gradientTop = new THREE.Color(top);
    const gradientBottom = new THREE.Color(bottom);
    const vertexColors = new Float32Array(4 * 3);

    // Bottom vertices
    vertexColors[0] = gradientBottom.r; vertexColors[1] = gradientBottom.g; vertexColors[2] = gradientBottom.b;
    vertexColors[3] = gradientBottom.r; vertexColors[4] = gradientBottom.g; vertexColors[5] = gradientBottom.b;
    // Top vertices
    vertexColors[6] = gradientTop.r; vertexColors[7] = gradientTop.g; vertexColors[8] = gradientTop.b;
    vertexColors[9] = gradientTop.r; vertexColors[10] = gradientTop.g; vertexColors[11] = gradientTop.b;

    geo.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
    return geo;
  }, [baseColor]);

  return (
    <mesh position={[0, 0, -5]} geometry={geometry}>
      <meshBasicMaterial vertexColors side={THREE.FrontSide} />
    </mesh>
  );
}

interface AnimatedCardProps {
  frontImageUrl?: string;
  backImageUrl?: string;
  cardColor?: CardColor;
  whiteTreatment?: WhiteTreatment;
  showCover?: boolean;
  isAutoAnimatingRef: React.MutableRefObject<boolean>;
  rotationY: React.MutableRefObject<number>;
  rotationX: React.MutableRefObject<number>;
  positionY: React.MutableRefObject<number>;
  scale: React.MutableRefObject<number>;
}

function AnimatedCard({ frontImageUrl, backImageUrl, cardColor, whiteTreatment, showCover, isAutoAnimatingRef, rotationY, rotationX, positionY, scale }: AnimatedCardProps) {
  const startTime = useRef(Date.now());

  useFrame(() => {
    if (isAutoAnimatingRef.current) {
      const elapsed = Date.now() - startTime.current;

      // Floating animation
      positionY.current = Math.sin((elapsed / FLOAT_PERIOD) * Math.PI * 2) * FLOAT_AMPLITUDE;

      // Rotation animation
      rotationY.current = INITIAL_ROTATION_Y + Math.sin((elapsed / ROTATE_PERIOD) * Math.PI * 2) * ROTATE_AMPLITUDE;
      rotationX.current = INITIAL_TILT_X;
    }
  });

  return (
    <CardModel
      frontImageUrl={frontImageUrl}
      backImageUrl={backImageUrl}
      cardColor={cardColor}
      whiteTreatment={whiteTreatment}
      showCover={showCover}
      rotationY={rotationY}
      rotationX={rotationX}
      positionY={positionY}
      scale={scale}
    />
  );
}

interface CardCreatorCanvasProps {
  frontImageUrl?: string;
  backImageUrl?: string;
  cardColor?: CardColor;
  whiteTreatment?: WhiteTreatment;
  showCover?: boolean;
  backgroundColor?: string;
  environment?: EnvironmentPreset;
}

export default function CardCreatorCanvas({ frontImageUrl, backImageUrl, cardColor = 'white', whiteTreatment = 'glossy', showCover = false, backgroundColor = DEFAULT_BG_COLOR, environment = 'apartment' }: CardCreatorCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isAutoAnimating = useRef(true);
  const isMouseDown = useRef(false);
  const hasDragged = useRef(false);
  const lastMousePos = useRef({ x: 0, y: 0 });

  const rotationY = useRef(INITIAL_ROTATION_Y);
  const rotationX = useRef(INITIAL_TILT_X);
  const positionY = useRef(0);
  const scale = useRef(1);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isMouseDown.current = true;
    hasDragged.current = false;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isMouseDown.current) return;

    const deltaX = e.clientX - lastMousePos.current.x;
    const deltaY = e.clientY - lastMousePos.current.y;

    // Only count as drag if moved more than a few pixels
    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
      hasDragged.current = true;
      isAutoAnimating.current = false;
    }

    if (hasDragged.current) {
      rotationY.current += deltaX * ROTATION_SENSITIVITY;

      const newRotationX = rotationX.current - deltaY * ROTATION_SENSITIVITY;
      rotationX.current = Math.max(-MAX_TILT, Math.min(MAX_TILT, newRotationX));
    }

    lastMousePos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseUp = useCallback(() => {
    isMouseDown.current = false;
  }, []);

  const handleClick = useCallback(() => {
    // Only re-enable auto animation if it was a click (not a drag)
    if (!hasDragged.current) {
      isAutoAnimating.current = true;
    }
  }, []);

  // Handle mouse leaving the canvas and wheel events
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      isMouseDown.current = false;
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);

    // Attach wheel handler directly to prevent page scroll
    const container = containerRef.current;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Scroll/pinch to zoom
      const delta = e.deltaY * ZOOM_SENSITIVITY;
      const newScale = scale.current - delta;
      scale.current = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    };

    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', cursor: isMouseDown.current ? 'grabbing' : 'grab' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
    >
      <Canvas
        shadows
        camera={{ position: [0, 1, 5], rotation: [-0.2, 0, 0] }}
        gl={{ alpha: true, antialias: true }}
      >
        <GradientBackground baseColor={backgroundColor} />
        <Environment preset={environment} environmentIntensity={0.4} background={false} />
        <ambientLight intensity={0.05} color="#b0c0d0" />

        {/* Key light - warm sunlight from upper-right */}
        <directionalLight
          position={[5, 10, 5]}
          intensity={3.5}
          color="#fff5e0"
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />

        {/* Fill light from front */}
        <directionalLight position={[0, 3, 4]} intensity={0.8} color="#d0e0f0" />

        <Suspense fallback={null}>
          <AnimatedCard
            frontImageUrl={frontImageUrl}
            backImageUrl={backImageUrl}
            cardColor={cardColor}
            whiteTreatment={whiteTreatment}
            showCover={showCover}
            isAutoAnimatingRef={isAutoAnimating}
            rotationY={rotationY}
            rotationX={rotationX}
            positionY={positionY}
            scale={scale}
          />
        </Suspense>
      </Canvas>
    </div>
  );
}
